import { motion } from 'framer-motion';
import { useAtom } from 'jotai';
import { useAtomValue, useUpdateAtom } from 'jotai/utils';
import { useCallback, useEffect, useMemo } from 'react';
import { useCollectionQuery } from 'src/api/queries/collectionsQueries';
import {
  BatchActions,
  ListingCard,
  ListingCardShimmer,
  ListingRow,
  PageTitle,
  Shimmer,
  LayoutViewSwitcher,
  LayoutView,
} from 'src/components';
import { useAccessToken } from 'src/hooks/useAccessToken';
import useLayoutSwitcher from 'src/hooks/useLayoutSwitcher';
import { deletedAssetsAtom } from 'src/shared/deletedAssets';
import { isRefetchingAtom, refreshAtom } from 'src/shared/refresh';
import { selectedAssetsAtom } from 'src/shared/selectedAssets';
import { AgentNudgeRow } from 'src/chat/nudges';
import { getThumbnailUrl, thumbnailVersion } from 'src/utils/previews';
import styles from './styles.module.scss';
import BoardingTour from 'src/components/BoardingTour';
import { useRememberContentTypes } from 'src/chat/useRememberContentTypes';

const Basket = () => {
  const { isLayoutList, isLayoutGrid } = useLayoutSwitcher();

  const refreshValue = useAtomValue(refreshAtom);
  const setIsRefetching = useUpdateAtom(isRefetchingAtom);
  const [selectedAssets, updateSelectedAssets] = useAtom(selectedAssetsAtom);
  const [deletedAssets, updateDeletedAssets] = useAtom(deletedAssetsAtom);

  const { isLoading, data: fetchedData, refetch, isRefetching } = useCollectionQuery();
  const accessToken = useAccessToken();

  useEffect(() => {
    updateDeletedAssets([]);
  }, []);

  const basket = useMemo(() => {
    if (!fetchedData) return fetchedData;

    const assets = fetchedData?.data?.assets.filter((asset: string) => !deletedAssets.includes(asset));

    return { data: { ...fetchedData?.data, assets } };
  }, [fetchedData, deletedAssets]);

  const handleOnAssetSelectChange = (assetId: string) => {
    const newSelectedAssets = selectedAssets.includes(assetId)
      ? selectedAssets.filter((id) => id !== assetId)
      : [...selectedAssets, assetId];
    updateSelectedAssets(newSelectedAssets);
  };

  const handleSelectAllAssets = (selected: boolean) => {
    const newSelectedAssets = selected ? basket.data?.assets : [];
    updateSelectedAssets(newSelectedAssets);
  };

  const basketAssetIds: string[] = useMemo(() => basket?.data?.assets ?? [], [basket]);

  /*
   * The nudge row acts on the ticked assets, or — when nothing is ticked — on
   * the whole basket.
   *
   * Falling back to the whole basket is the point of having the row here: a
   * basket IS a deliberately gathered set, so "do this to my basket" is the
   * common intent and making the user tick all of it first would be busywork.
   * The dashboard has no equivalent fallback because "everything matching the
   * current filters" is not a set the user chose.
   *
   * Content types come from the collection payload the grid already renders
   * from: the ['asset', id] query cache is only warm for assets whose detail
   * modal has been opened, so it would report 'unknown' for most of a basket
   * and silently drop the image/video-gated chips.
   */
  const nudgeAssetIds = selectedAssets.length > 0 ? selectedAssets : basketAssetIds;
  useRememberContentTypes(basket?.data?.extended_result);
  const contentTypesFor = useCallback(
    (ids: string[]) => ids.map((id) => basket?.data?.extended_result?.[id]?.content_type ?? ''),
    [basket],
  );

  useEffect(() => {
    if (isRefetching) return;
    refetch();
  }, [refreshValue]);

  useEffect(() => {
    setIsRefetching(isRefetching);
  }, [isRefetching]);

  useEffect(() => {
    return () => {
      if (selectedAssets.length > 0) {
        updateSelectedAssets([]);
      }
    };
  }, []);

  return (
    <><BoardingTour title='Basket' description='This page shows the assets that are in the baskets' path='/basket' uniqueId='BASKET' />
    <div className={styles.pageWrapper}>
      <div className={styles.pageContent}>
        <PageTitle title="Basket" counter={basket?.data?.assets.length} />
        <div className={styles.actions}>
          <BatchActions onSelectAll={handleSelectAllAssets} showSelectAll={basket?.data?.assets.length > 0} />

          <LayoutViewSwitcher />
        </div>

        <AgentNudgeRow
          surface="selection"
          assetIds={nudgeAssetIds}
          contentTypes={contentTypesFor(nudgeAssetIds)}
          // Only when the row stands for the whole basket: the prompts say
          // "the selected assets", so the agent's selection context has to be
          // pointed at them. With a real tick-selection the atom already holds
          // the truth and must not be disturbed.
          claimSelection={selectedAssets.length === 0}
        />

        <LayoutView
          data={basket?.data?.assets}
          isLoading={isLoading}
          emptyDataMessage="No assets in basket"
          classNames={{
            cardView: 'listingGridWrapper',
          }}
          cardView={(assetId: string) => (
            <ListingCard
              key={assetId}
              id={assetId}
              siblings={basket.data?.result}
              imageUrl={getThumbnailUrl({
                width: 300,
                height: 300,
                id: assetId,
                jwt: accessToken,
                version: thumbnailVersion(basket.data?.extended_result?.[assetId]),
              })}
              name={basket.data?.extended_result[assetId]?.name}
              size={basket.data?.extended_result[assetId]?.size}
              contentType={basket.data?.extended_result[assetId]?.content_type}
              trashed={basket.data?.extended_result[assetId]?.trashed}
              status={basket?.data?.extended_result[assetId]?.status}
              onSelectChange={() => handleOnAssetSelectChange(assetId)}
              isSelected={selectedAssets.includes(assetId)}
              isGenerated={!!basket?.data?.extended_result[assetId]?.data?._filespin_original_asset_id}
              versions={basket?.data?.extended_result[assetId]?.versions}
              originalAssetId={basket?.data?.extended_result[assetId]?.data?._filespin_original_asset_id}
              error={basket?.data?.extended_result[assetId]?.errors}
            />
          )}
          listView={(assetId: string) => (
            <ListingRow
              key={assetId}
              id={assetId}
              siblings={basket.data?.result}
              imageUrl={getThumbnailUrl({
                width: 100,
                height: 100,
                id: assetId,
                jwt: accessToken,
                version: thumbnailVersion(basket.data?.extended_result?.[assetId]),
              })}
              name={basket.data?.extended_result[assetId]?.name}
              size={basket.data?.extended_result[assetId]?.size}
              uploadTime={basket.data?.extended_result[assetId]?.upload_time}
              contentType={basket.data?.extended_result[assetId]?.content_type}
              trashed={basket.data?.extended_result[assetId]?.trashed}
              status={basket?.data?.extended_result[assetId]?.status}
              onSelectChange={() => handleOnAssetSelectChange(assetId)}
              isSelected={selectedAssets.includes(assetId)}
              isGenerated={!!basket?.data?.extended_result[assetId]?.data?._filespin_original_asset_id}
              versions={basket?.data?.extended_result[assetId]?.versions}
              originalAssetId={basket?.data?.extended_result[assetId]?.data?._filespin_original_asset_id}
              error={basket?.data?.extended_result[assetId]?.errors}
            />
          )}
        />
      </div>
    </div>
    </>
  );
};

export default Basket;
